import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import addDays from 'date-fns/addDays';
import Button from 'products/nomad_insurance/components/Button';
import { checkIfUserIsAdult } from 'products/nomad_insurance/helpers';
import AdulthoodConfirmation from './components/AdulthoodConfirmation';
import { setPreviousApplicantIds, setValidity } from './state';
import { GroupMembersTitle, GroupMembersList } from './styled';
import Applicant from '../../components/Applicant';
import AddApplicant from '../../components/AddApplicant';
import { useStepNavigationContext } from '../../context';
import StepView from '../../components/StepView';
import Accordion from '../../components/Accordion';
import { Title, InfoText, MediumText, NextRow } from '../../styled';
import { trackHubSpotEvent } from '../../utils/hubspot/hubspot-custom-events';

const Applicants = () => {
  const dispatch = useDispatch();
  const { changeStep, jumpToFirstStep, isRenewal } = useStepNavigationContext();
  const [hasSeenAdultGroupMemberWarning, setHasSeenAdultGroupMemberWarning] =
    useState(false);
  const [isAdulthoodConfirmationOpen, setAdulthoodConfirmationOpen] = useState(false);
  const { groupMembers, planholder } = useSelector(
    (state) => state.nomadInsurance.application
  );

  const { activePaidContracts } = useSelector((state) => state.nomadInsurance.contract);

  const previousGroupMembers = useMemo(() => {
    return (
      activePaidContracts?.[0]?.groupMemberContracts?.map(
        (contract) => contract.groupMember.groupMemberID
      ) || []
    );
  }, [activePaidContracts]);

  useEffect(() => {
    if (isRenewal) {
      dispatch(setPreviousApplicantIds(previousGroupMembers));
    }
  }, [previousGroupMembers]);

  const { isValid } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.destinations
  );
  const { hasEndDate, startDate, endDate } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );

  useEffect(() => {
    if (!isValid) {
      jumpToFirstStep();
    }
  }, [isValid]);

  const navigateToNextStep = () => {
    dispatch(setValidity(true));

    void trackHubSpotEvent('dependants', planholder.email);

    changeStep(1);
  };

  const onNextButtonClick = () => {
    const policyHasAdultMembers = groupMembers
      .map((member) => member.dateOfBirth)
      .some((dob) =>
        checkIfUserIsAdult(dob, hasEndDate ? endDate : addDays(new Date(startDate), 363))
      );

    if (policyHasAdultMembers && !hasSeenAdultGroupMemberWarning) {
      setAdulthoodConfirmationOpen(true);
      return;
    }
    navigateToNextStep();
  };

  const closeAdulthoodConfirmation = () => {
    setAdulthoodConfirmationOpen(false);
  };
  const applicants = groupMembers?.length > 0 ? groupMembers : [];

  return (
    <>
      <AdulthoodConfirmation
        isOpen={!!isAdulthoodConfirmationOpen}
        onClose={closeAdulthoodConfirmation}
        onConfirmation={navigateToNextStep}
      />
      <StepView>
        <Title>Who are you traveling with?</Title>
        <InfoText>
          You can add a travel companion or family member to your plan{' '}
          <MediumText>
            if you are traveling together and have the same home country
          </MediumText>
          . A group member costs the same as a separate policy and selected add-ons apply
          to all group members.
        </InfoText>
        <Accordion heading="Traveling with children?" childrenMarginTop="8px">
          <InfoText>
            You can add up to two children under 10 years of age, or one child per adult,
            for no extra cost. Regular pricing applies to additional children.
          </InfoText>
        </Accordion>
        {applicants?.length > 0 && (
          <GroupMembersTitle>Your group members</GroupMembersTitle>
        )}
        <GroupMembersList>
          {applicants?.map((groupMember, index) => (
            <Applicant
              key={groupMember.groupMemberID}
              groupMember={groupMember}
              memberPictureIndex={index}
            />
          ))}
          <AddApplicant onAddCallback={() => setHasSeenAdultGroupMemberWarning(true)} />
        </GroupMembersList>

        <NextRow>
          <Button onClick={onNextButtonClick} color="confetti">
            {applicants && applicants.length > 0 ? 'Next' : "I'm going solo"}
          </Button>
          <InfoText>Next up, review your information</InfoText>
        </NextRow>
      </StepView>
    </>
  );
};

export default Applicants;
